import { HubConnection } from '@microsoft/signalr/dist/esm/HubConnection';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { useApi } from '../utils/api';

export type ConnectionStatus =
    | 'connecting'
    | 'connected'
    | 'reconnecting'
    | 'disconnected';

type Handlers = {
    onSyncAppData: (msg: any) => void;
    onSyncUserSite: (msg: any) => void;
    onReconnected?: () => void;
};

export const useSignalRConnection = (handlers: Handlers) => {
    const api = useApi();
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [status, setStatus] = useState<ConnectionStatus>('connecting');

    // Refs so we don't rebuild the connection when callbacks change
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;

    useEffect(() => {
        let cancelled = false;
        let currentConnection: HubConnection | null = null;
        let restartTimeout: ReturnType<typeof setTimeout> | null = null;

        const startConnection = async () => {
            try {
                setStatus('connecting');
                const conn = await api.connect();
                currentConnection = conn;

                // Register handlers BEFORE start() so reconnects don't miss listeners
                conn.on('SyncAppData', (m) => handlersRef.current.onSyncAppData(m));
                conn.on('SyncUserSite', (m) => handlersRef.current.onSyncUserSite(m));

                conn.onreconnecting((error) => {
                    console.warn('SignalR reconnecting:', error);
                    Sentry.captureMessage('signalr_reconnecting', {
                        level: 'warning',
                        extra: { error: error?.message },
                    });
                    setStatus('reconnecting');
                });

                conn.onreconnected((connectionId) => {
                    console.log('SignalR reconnected:', connectionId);
                    Sentry.captureMessage('signalr_reconnected', {
                        level: 'info',
                        extra: { connectionId },
                    });
                    setStatus('connected');
                    handlersRef.current.onReconnected?.();
                });

                // Critical: when auto-reconnect gives up, onclose fires.
                // We manually restart so the client NEVER permanently dies.
                conn.onclose((error) => {
                    console.error('SignalR closed:', error);
                    Sentry.captureMessage('signalr_closed', {
                        level: 'error',
                        extra: { error: error?.message },
                    });
                    setStatus('disconnected');
                    if (!cancelled) {
                        restartTimeout = setTimeout(() => {
                            if (!cancelled) startConnection();
                        }, 5000);
                    }
                });

                await conn.start();
                if (cancelled) {
                    conn.stop().catch(() => {});
                    return;
                }

                console.log('SignalR connected:', conn.connectionId);
                setStatus('connected');
                setConnection(conn);

                // Treat initial connect like a reconnect for catch-up
                handlersRef.current.onReconnected?.();
            } catch (e) {
                console.error('SignalR connection failed:', e);
                Sentry.captureException(e);
                setStatus('disconnected');
                if (!cancelled) {
                    restartTimeout = setTimeout(() => {
                        if (!cancelled) startConnection();
                    }, 5000);
                }
            }
        };

        startConnection();

        return () => {
            cancelled = true;
            if (restartTimeout) clearTimeout(restartTimeout);
            if (currentConnection) {
                currentConnection.stop().catch(() => {});
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { connection, status };
};
